import { Card } from "@/components/ui/card";
import { Upload, Sparkles, Edit, Mail } from "lucide-react";

const steps = [
  {
    number: "1",
    icon: Upload,
    title: "Upload Photo",
    description: "Snap or upload a photo of the job site, materials, or completed work from your mobile device.",
    color: "bg-primary",
  },
  {
    number: "2",
    icon: Sparkles,
    title: "AI Analyzes",
    description: "Our AI examines the photo and creates a detailed quote with materials, quantities, and estimated costs.",
    color: "bg-accent",
  },
  {
    number: "3",
    icon: Edit,
    title: "Review & Edit",
    description: "Fine-tune the quote, adjust prices, add labor costs, and customize line items before generating the invoice.",
    color: "bg-success",
  },
  {
    number: "4",
    icon: Mail,
    title: "Send & Get Paid",
    description: "Instantly send the professional PDF invoice via Telegram, email, or WhatsApp. Track delivery and get paid faster.",
    color: "bg-purple-500",
  },
];

const HowItWorks = () => {
  return (
    <section className="py-24 px-4 bg-secondary relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-20 animate-fade-in-up">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-6">
            How SnapBill Works
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Four simple steps to professional invoicing
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className="relative animate-fade-in-up"
              style={{ animationDelay: `${index * 0.15}s`, animationFillMode: 'both' }}
            >
              {/* Connection Line with Arrow */}
              {index < steps.length - 1 && (
                <div className="hidden lg:flex absolute top-20 left-full w-full items-center justify-center z-0" 
                     style={{ transform: 'translateX(-50%)' }}
                >
                  <div className="w-full h-0.5 bg-gradient-to-r from-border via-primary/20 to-border" />
                  <div className="absolute right-0 w-2 h-2 bg-primary rounded-full" />
                </div>
              )}
              
              <Card className="relative p-8 hover:shadow-xl transition-all duration-500 bg-card h-full z-10 group hover:-translate-y-2 hover:border-primary/30 border-2">
                <div className="flex flex-col items-center text-center space-y-5">
                  {/* Step Number Badge */}
                  <div className={`${step.color} text-white w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {step.number}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors duration-300">
                    <step.icon className="h-10 w-10 text-primary group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  
                  {/* Content */}
                  <div className="space-y-3">
                    <h3 className="text-xl font-display font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
